import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { imgUrl } from "../lib/api";
import { to12h } from "../lib/format";
import Countdown from "./Countdown";
import { Bell, BellRing } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { BookPeek } from "./BookPeek";

/* Home hero: Kerala · Golden Raja · Dear lifted to the top — each with show-time chips to pick which show to advance-book. */
export const TOP_BRANDS = ["Kerala", "Dear", "Golden Raja"];
const PUB = process.env.PUBLIC_URL || "";
const V = "?v=3";
const STYLE = {
  Kerala: { bg: `linear-gradient(90deg, rgba(4,47,36,.92) 0%, rgba(4,47,36,.72) 38%, rgba(4,47,36,.18) 70%, rgba(4,47,36,.05) 100%), url(${PUB}/img/brands/kerala.jpg${V}) center / cover no-repeat`, sub: "text-emerald-200", tag: "Kerala Lottery", punch: "ஒரு எண் · ஒரு வாழ்க்கை மாற்றம்", punchEn: "One number can change the day" },
  Dear: { bg: `linear-gradient(90deg, rgba(76,5,25,.92) 0%, rgba(76,5,25,.72) 38%, rgba(76,5,25,.18) 70%, rgba(76,5,25,.05) 100%), url(${PUB}/img/brands/dear.jpg${V}) center / cover no-repeat`, sub: "text-rose-200", tag: "Dear Lottery", punch: "இன்று உன் நாள் · தைரியமா புக் பண்ணு", punchEn: "Today is your day · book with courage" },
  "Golden Raja": { bg: `linear-gradient(90deg, rgba(69,26,3,.92) 0%, rgba(69,26,3,.72) 38%, rgba(69,26,3,.18) 70%, rgba(69,26,3,.05) 100%), url(${PUB}/img/brands/golden-raja.jpg${V}) center / cover no-repeat`, sub: "text-amber-200", tag: "Golden Raja", punch: "ராஜா மாதிரி ஆடு · ராஜா மாதிரி ஜெயி", punchEn: "Play like a king · win like a king" },
};

const RecentStrip = ({ cat, items, ta }) => {
  if (!items?.length) return null;
  const slug = cat.toLowerCase().replace(/\s+/g, "-");
  return (
    <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid={`brand-recent-${slug}`}>
      <span className="text-[9px] uppercase tracking-wider opacity-70">{ta ? "கடைசி 3 results:" : "Last 3 results:"}</span>
      {items.map((r, i) => (
        <span key={`${r.draw_date}-${r.draw_time}-${i}`} className="inline-flex items-center gap-1 rounded-md bg-black/25 border border-white/15 px-1.5 py-0.5" title={`${r.game_name} · ${r.draw_date}`} data-testid={`brand-recent-${slug}-${i}`}>
          <span className="font-mono font-black text-[12px] tracking-widest text-amber-300">{r.winning_number}</span>
          <span className="text-[8px] opacity-70">{to12h(r.draw_time)} · {r.draw_date.slice(8)}/{r.draw_date.slice(5, 7)}</span>
        </span>
      ))}
    </div>
  );
};

const BrandCard = ({ cat, shows, image, ta, recent, bells, onBell }) => {
  const navigate = useNavigate();
  const st = STYLE[cat];
  const open = shows.filter((g) => g.runtime?.is_open);
  const next = open[0] || shows[0];
  const slug = cat.toLowerCase().replace(/\s+/g, "-");
  return (
    <div className="rounded-2xl p-3 pt-4 min-h-[172px] flex flex-col justify-end text-white shadow-lg relative overflow-hidden rl-gold-border" style={{ background: st.bg }} data-testid={`brand-card-${slug}`}>
      <div className="absolute inset-x-0 bottom-0 h-2/3 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent, rgba(0,0,0,.55))" }} />
      <BookPeek className="absolute top-2 right-3 opacity-70" tone="light" testId={`book-peek-${slug}`} />
      <div className="relative flex items-end gap-3">
        {image ? <img src={imgUrl(image)} alt={cat} className="w-14 h-14 rounded-xl object-cover rl-gold-border shrink-0 shadow-lg" /> : <div className="w-14 h-14 rounded-xl rl-gold-bg shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className={`text-[9px] uppercase tracking-widest font-bold ${st.sub} drop-shadow`}>{st.tag} · {shows.length === 1 ? (ta ? `தினமும் ${to12h(shows[0].draw_time)}` : `Daily ${to12h(shows[0].draw_time)}`) : (ta ? `${shows.length} shows / நாள்` : `${shows.length} shows a day`)}</div>
          <div className="font-display font-black text-xl leading-tight truncate drop-shadow-lg">{cat}</div>
          <div className="text-[10px] font-bold text-amber-200 italic truncate drop-shadow" data-testid={`brand-punch-${slug}`}>{ta ? st.punch : st.punchEn}</div>
          <div className="text-[11px] drop-shadow" data-testid={`brand-status-${slug}`}>
            {next?.runtime?.is_open ? <>{ta ? "Closes in" : "Closes in"} <Countdown seconds={next.runtime.closing_seconds} className="text-amber-300 font-bold" />{open.length > 1 ? ` · ${open.length} ${ta ? "shows open" : "shows open"}` : ""}</>
              : next?.last_result ? `${ta ? "கடைசி result" : "Last result"} ${next.last_result.winning_number}` : (ta ? "இன்று முடிந்தது" : "Closed for today")}
          </div>
        </div>
        <button onClick={() => navigate(`/play/${next.id}`)} className="rl-gold-bg text-[10px] font-black px-3 py-1.5 rounded-full shrink-0 active:scale-95 transition-transform" data-testid={`brand-book-${slug}`}>{next?.runtime?.is_open ? "BOOK NOW" : "VIEW"}</button>
      </div>
      <div className="relative mt-2.5 flex items-center gap-1.5 flex-wrap">
        <span className="text-[9px] uppercase tracking-wider opacity-70 mr-0.5">{ta ? "Advance book — show தேர்வு:" : "Advance book — pick show:"}</span>
        {shows.map((g) => {
          const isOpen = !!g.runtime?.is_open;
          const bell = bells.has(g.id);
          return (
            <span key={g.id} className={`inline-flex items-center rounded-full border ${isOpen ? "bg-white text-slate-900 border-white" : "bg-white/10 text-white/70 border-white/20"}`}>
              <button onClick={() => navigate(`/play/${g.id}`)} data-testid={`brand-show-${g.id}`} className="text-[11px] font-black pl-2.5 pr-1.5 py-1 active:scale-95 transition-transform">
                {to12h(g.draw_time)}{isOpen ? <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse align-middle" /> : (g.last_result ? <span className="ml-1 opacity-70">· {g.last_result.winning_number}</span> : null)}
              </button>
              <button onClick={() => onBell(g)} title={bell ? (ta ? "Reminder ON — tap to remove" : "Reminder ON — tap to remove") : (ta ? "Close-க்கு 10 நிமிடம் முன் நினைவூட்டு" : "Remind me 10 min before close")} data-testid={`brand-bell-${g.id}`} data-on={bell ? "1" : "0"}
                className={`pr-2 pl-1 py-1 border-l ${isOpen ? "border-slate-300" : "border-white/20"} active:scale-90 transition-transform`}>
                {bell ? <BellRing className="w-3.5 h-3.5 text-amber-500" /> : <Bell className={`w-3.5 h-3.5 ${isOpen ? "text-slate-500" : "text-white/60"}`} />}
              </button>
            </span>
          );
        })}
      </div>
      <RecentStrip cat={cat} items={recent} ta={ta} />
    </div>
  );
};

export const TopBrands = ({ games, catImages, ta }) => {
  const { user } = useAuth();
  const [recent, setRecent] = useState({});
  const [bells, setBells] = useState(new Set());
  useEffect(() => { api.get("/results/brand-recent", { params: { cats: TOP_BRANDS.join(","), n: 3 } }).then((r) => setRecent(r.data || {})).catch(() => {}); }, []);
  useEffect(() => { if (!user) return; api.get("/reminders").then((r) => setBells(new Set(r.data.game_ids || []))).catch(() => {}); }, [user]);
  const onBell = async (g) => {
    if (!user) return toast.error(ta ? "Reminder-க்கு login செய்யவும்" : "Login to set a reminder");
    try {
      const { data } = await api.post("/reminders/toggle", { game_id: g.id });
      setBells((s) => { const n = new Set(s); data.on ? n.add(g.id) : n.delete(g.id); return n; });
      toast.success(data.on ? (ta ? `🔔 ${g.name} close-க்கு 10 நிமிடம் முன் நினைவூட்டுவோம்` : `🔔 We'll remind you 10 min before ${g.name} closes`) : (ta ? "Reminder நீக்கப்பட்டது" : "Reminder removed"));
    } catch { toast.error("Failed"); }
  };
  const cards = TOP_BRANDS.map((cat) => ({ cat, shows: games.filter((g) => g.category === cat && g.mode !== "interval").sort((a, b) => a.draw_time.localeCompare(b.draw_time)) })).filter((c) => c.shows.length);
  if (!cards.length) return null;
  return (
    <div className="space-y-2.5" data-testid="top-brands">
      {cards.map((c) => <BrandCard key={c.cat} cat={c.cat} shows={c.shows} image={catImages[c.cat]} ta={ta} recent={recent[c.cat]} bells={bells} onBell={onBell} />)}
    </div>
  );
};
